import React, { useEffect, useRef, useState } from 'react';

import {
  Box,
  Divider,
  HStack,
  Icon,
  Image,
  Stack,
  useBreakpointValue,
} from '@chakra-ui/react';
import {
  NavGroup,
  Sidebar,
  SidebarSection,
  SidebarToggleButton,
} from '@saas-ui/react';
import { MenuIcon, XIcon } from 'lucide-react';

import { bottomMenuItems, HIGHEST_Z_INDEX, menuItems } from '@/constant';
import { usePromptsEnabled } from '@/store/prompts';
import { useIsWorksheetPassAdmin } from '@/store/worksheetPass';

import { AppSidebarLink } from './AppSidebarLink';
import { UserMenu } from './UserMenu';

const SIDEBAR_WIDTH_EXPANDED = 60;
const SIDEBAR_WIDTH_COLLAPSED = '73px';
const SIDEBAR_HOVER_CLOSE_DELAY_MS = 150;

interface AppSidebarProps {
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (isMobileMenuOpen: boolean) => void;
}

export const AppSidebar: React.FC<AppSidebarProps> = props => {
  const { isMobileMenuOpen, setIsMobileMenuOpen } = props;

  const isAdmin = useIsWorksheetPassAdmin();
  const isPromptsEnabled = usePromptsEnabled();

  const isMedium = useBreakpointValue(
    { base: true, xl: false },
    { fallback: 'xl' }
  );
  const isLarge = useBreakpointValue(
    { base: false, xl: true },
    { fallback: 'xl' }
  );
  const [isHovered, setIsHovered] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const hoverLeaveTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);

  const isPinned = isMobileMenuOpen;
  const isExpanded = isLarge
    ? isPinned || isHovered || isUserMenuOpen
    : isMobileMenuOpen;

  const handleNavItemClick = () => {
    if (!isLarge) {
      setIsMobileMenuOpen(false);
    }
  };

  const handleMouseEnter = () => {
    if (!isLarge) return;
    if (hoverLeaveTimeoutRef.current) {
      clearTimeout(hoverLeaveTimeoutRef.current);
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (!isLarge) return;
    hoverLeaveTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, SIDEBAR_HOVER_CLOSE_DELAY_MS);
  };

  useEffect(() => {
    if (isMedium) setIsMobileMenuOpen(false);
  }, [isMedium, setIsMobileMenuOpen]);

  useEffect(() => {
    if (isLarge || !isMobileMenuOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isLarge, isMobileMenuOpen, setIsMobileMenuOpen]);

  useEffect(() => {
    return () => {
      if (hoverLeaveTimeoutRef.current) {
        clearTimeout(hoverLeaveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box
      display={{ base: 'contents', xl: 'block' }}
      position={{ xl: 'relative' }}
      width={{
        xl: isPinned ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED,
      }}
      flexShrink={{ xl: 0 }}
      height={{ xl: 'full' }}
      alignSelf={{ xl: 'stretch' }}
      overflow={{ xl: 'visible' }}
      zIndex={{
        xl: isExpanded ? HIGHEST_Z_INDEX : 1,
      }}
    >
      {!isLarge && isMobileMenuOpen ? (
        <Box
          as='button'
          type='button'
          aria-label='Close navigation'
          data-cy='sidebar-overlay'
          position='absolute'
          inset={0}
          width='100%'
          height='100%'
          padding={0}
          bg='blackAlpha.900'
          zIndex='overlay'
          border='0'
          cursor='pointer'
          onClick={() => setIsMobileMenuOpen(false)}
        />
      ) : null}
      <Sidebar
        // The mobile drawer slides on an inline left offset that the desktop
        // rail never animates away, so pin it back at xl. Base tracks the
        // drawer state, since setting left here replaces the library rule.
        left={{
          base: isMobileMenuOpen ? '0' : '-100%',
          xl: '0 !important',
        }}
        zIndex={{
          base: HIGHEST_Z_INDEX,
          xl: isExpanded ? HIGHEST_Z_INDEX : 1,
        }}
        minWidth='auto'
        width={{
          base: 'full',
          xl: isExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED,
        }}
        height={{ xl: 'full' }}
        overflow='visible'
        gap={{ base: 4, xl: 3 }}
        paddingTop={{ base: '0px !important', xl: '3 !important' }}
        paddingBottom={{ base: 4, xl: 3 }}
        transition={{ base: 'none', xl: 'all' }}
        transitionDuration={{ base: '0ms', xl: '200ms' }}
        transitionTimingFunction={{ base: 'none', xl: 'ease-in-out' }}
        toggleBreakpoint='xl'
        border='1px solid'
        motionPreset={isLarge ? 'none' : 'slideInOut'}
        onOpen={() => {
          if (!isLarge) {
            setIsMobileMenuOpen(true);
          }
        }}
        onClose={() => {
          if (!isLarge) {
            setIsMobileMenuOpen(false);
          }
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        variant={isLarge ? (isExpanded ? 'default' : 'compact') : undefined}
        isOpen={isLarge ? true : isMobileMenuOpen}
      >
        <SidebarSection
          display={{ base: 'flex', xl: 'none' }}
          gap={4}
          px={4}
          pt={4}
          position='sticky'
          top={0}
          margin='0 !important'
        >
          <HStack justifyContent='space-between'>
            <Image
              src='https://cdn.prod.website-files.com/66f62ac0b4dbc96bb348eb73/694811546c1d9a052e5631f8_VH3AI%20-%20Main%20Logo%20-%20Color.png'
              alt='VH3 AI'
              h='22px'
              w='auto'
              objectFit='contain'
            />
            {isMobileMenuOpen ? (
              <SidebarToggleButton
                data-cy='sidebar-toggle-button'
                position='relative'
                size='xs'
                icon={<XIcon size={24} />}
                top={2}
                opacity={0.3}
                _hover={{
                  bgColor: 'transparent',
                  opacity: 1,
                }}
                _active={{
                  bgColor: 'transparent',
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              />
            ) : (
              <SidebarToggleButton
                data-cy='sidebar-toggle-button'
                size='sm'
                icon={<Icon as={MenuIcon} boxSize={{ base: 26, xl: 24 }} />}
                top={2}
                opacity={0.3}
                left={{ base: 4, md: 6 }}
                _hover={{
                  bgColor: 'transparent',
                  opacity: 1,
                }}
                _active={{
                  bgColor: 'transparent',
                }}
                onClick={() => setIsMobileMenuOpen(true)}
              />
            )}
          </HStack>
          <Divider display={{ base: 'block', xl: 'none' }} />
        </SidebarSection>
        <SidebarSection
          margin='0 !important'
          overflowX='hidden'
          overflowY='auto'
          minHeight={120}
          flex='1'
          paddingInline={{ base: 4, xl: 3 }}
        >
          <NavGroup>
            {menuItems
              .filter(
                item =>
                  !item.hidden && (!item.requiresPrompts || isPromptsEnabled)
              )
              .map(item => (
                <AppSidebarLink
                  borderColor='transparent!'
                  key={item.id}
                  href={item.url}
                  exact={item.exact}
                  label={item.label}
                  navIcon={item.icon}
                  isMobileMenuOpen={isExpanded}
                  isDisabled={item.disabled}
                  onClick={handleNavItemClick}
                />
              ))}
          </NavGroup>
        </SidebarSection>
        <SidebarSection
          gap={{ base: 4, xl: 3 }}
          paddingInline={{ base: 4, xl: 3 }}
          margin='0 !important'
          overflow='visible'
        >
          <NavGroup marginBottom='0 !important'>
            {bottomMenuItems
              .filter(item => !item.hidden && (!item.adminOnly || isAdmin))
              .map(item => (
                <AppSidebarLink
                  borderColor='transparent!'
                  key={item.id}
                  href={item.url}
                  exact={item.exact}
                  label={item.label}
                  navIcon={item.icon}
                  isMobileMenuOpen={isExpanded}
                  isDisabled={item.disabled}
                  onClick={handleNavItemClick}
                />
              ))}
          </NavGroup>
          <Divider />
          <Stack gap={3} alignItems='stretch' overflow='visible'>
            <UserMenu
              isMobileMenuOpen={isExpanded}
              onNavClick={handleNavItemClick}
              onOpenChange={setIsUserMenuOpen}
            />
          </Stack>
        </SidebarSection>
      </Sidebar>
    </Box>
  );
};
